/**
 * What becomes of one link whose words a card changes: the instruction the edit model
 * gave on the replacement that changed them (`link` on `replace_span` and
 * `replace_paragraph`), carried from the web's apply (`EditOperations`) onto the card
 * paragraph (`RedlineTextParagraph.linkInstructions`) and into the engine that writes
 * the card (`RedlineEngine`, `LinkFieldStriker`).
 *
 * The link is named by its display text and its address, as the reader read them
 * (`ParagraphLinks.readingOf`): the engine reads the paragraph again where it writes,
 * and finds the link there by the same two.
 *
 * CASES, by `action`
 *   - "keep"      -> the new words stay inside the link, at its address
 *   - "change"    -> the old link is struck whole, and the new words written as a link to `newAddress`
 *   - "needs_url" -> the old link is struck whole, and the new words written as plain text; the card
 *                    says so and a Word comment on the new words names the link they replaced
 */
import { z } from "zod";

/** The three instructions a replacement over a link's words carries (`link`). */
export const linkActionSchema = z.enum(["keep", "change", "needs_url"]);

export type LinkAction = z.infer<typeof linkActionSchema>;

/** The link an instruction is for, and the words the replacement writes where it stood. */
const instructedLinkFields = {
    /** The link's display text as the reader read it. */
    linkText: z.string(),
    /**
     * The link's address as the reader read it.
     *
     * NULL
     *   The reader could not know it (`ParagraphLink.address`).
     */
    linkAddress: z.string().nullable(),
    /** The words the replacement that changed the link writes, as the model gave them: what the card shows and the comment sits on. */
    writtenText: z.string(),
};

export const linkInstructionSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("keep"), ...instructedLinkFields }),
    z.object({ action: z.literal("change"), ...instructedLinkFields, newAddress: z.string().min(1) }),
    z.object({ action: z.literal("needs_url"), ...instructedLinkFields }),
]);

export type LinkInstruction = z.infer<typeof linkInstructionSchema>;

/** No instruction for any link: the engine judges each link the edits reach by its words, as before instructions existed. */
export const NO_LINK_INSTRUCTIONS: readonly LinkInstruction[] = [];
